/**
 * Payment Terms Formatting Utilities
 * Handles conversion of payment terms from API format to user-friendly display format
 */

/**
 * Formats payment terms from API format to readable format
 * @param paymentTerms - Payment terms from API (e.g., "net_60", "net_30", "due_on_receipt")
 * @returns Formatted payment terms for display (e.g., "60 days", "30 days", "Upon receipt")
 */
export function formatPaymentTerms (paymentTerms: string): string {
  if (!paymentTerms) {
    return '-';
  }

  // Handle different payment terms formats
  switch (paymentTerms.toLowerCase()) {
    case 'net_30':
      return '30 days';
    case 'net_60':
      return '60 days';
    case 'net_90':
      return '90 days';
    case 'net_15':
      return '15 days';
    case 'net_45':
      return '45 days';
    case 'due_on_receipt':
    case 'due_on_invoice':
      return 'Upon receipt';
    case 'immediate':
      return 'Immediate';
    case 'cash':
      return 'Cash';
    case 'credit':
      return 'Credit';
    default:
      // If it's already in a readable format, return as is
      if (paymentTerms.includes('days') || paymentTerms.includes('day')) {
        return paymentTerms;
      }

      // If it contains numbers, try to extract and format
      const numberMatch = paymentTerms.match(/(\d+)/);
      if (numberMatch) {
        const days = numberMatch[1];
        return `${days} days`;
      }

      // Return original value if no pattern matches
      return paymentTerms;
  }
}

/**
 * Gets payment terms description for tooltips or additional info
 * @param paymentTerms - Payment terms from API
 * @returns Description of payment terms
 */
export function getPaymentTermsDescription (paymentTerms: string): string {
  if (!paymentTerms) {
    return '';
  }

  const formatted = formatPaymentTerms(paymentTerms);

  if (formatted.includes('days')) {
    return `Payment is due within ${formatted} of invoice date`;
  } else if (formatted === 'Upon receipt') {
    return 'Payment is due immediately upon receipt of invoice';
  } else if (formatted === 'Immediate') {
    return 'Payment is due immediately';
  }

  return '';
}

/**
 * Validates if payment terms are overdue
 * @param dueDate - Due date string
 * @param currentDate - Current date (optional, defaults to now)
 * @returns Object with overdue status and days overdue
 */
export function checkPaymentTermsOverdue (dueDate: string, currentDate: Date = new Date()) {
  if (!dueDate) {
    return {isOverdue: false, daysOverdue: 0};
  }

  const due = new Date(dueDate);
  const now = currentDate;
  const diffTime = now.getTime() - due.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  return {isOverdue: diffDays > 0, daysOverdue: Math.max(0, diffDays)};
}
